/**
 * Statement Analyzer Page - Auxilio Finance
 * AI-powered interpretation of personal financial statements
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  SparklesIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ClockIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  BanknotesIcon,
  AcademicCapIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';

interface StatementType {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface BalanceInfo {
  current_balance: number | null;
  previous_balance: number | null;
  minimum_payment: number | null;
  credit_limit: number | null;
  available_credit: number | null;
  payment_due_date: string | null;
}

interface Analysis {
  id: string;
  statement_type: string;
  analysis_date: string;
  summary: string;
  total_expenses: number;
  total_income: number;
  total_fees: number;
  total_transfers: number;
  transaction_count: number;
  category_breakdown: Record<string, number>;
  insights: string[];
  opportunities: string[];
  warnings: string[];
  action_items: string[];
  balance_info?: BalanceInfo;
  extraction_method?: string;
  institution?: string;
  transactions?: Array<{
    date: string;
    description: string;
    amount: number;
    category: string;
    type: string;
  }>;
}

interface HistoryItem {
  id: string;
  statement_type: string;
  analysis_date: string;
  total_expenses: number;
  total_income: number;
  transaction_count: number;
  summary: string;
}

const CATEGORY_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

export const StatementAnalyzer: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [activeTab, setActiveTab] = useState<'analyze' | 'history'>('analyze');
  const [statementTypes, setStatementTypes] = useState<StatementType[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [statementText, setStatementText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load statement types on mount
  useEffect(() => {
    loadStatementTypes();
    loadHistory();
  }, []);

  const loadStatementTypes = async () => {
    try {
      const response = await apiClient.get('/statement-analyzer/types');
      setStatementTypes(response.data.types);
    } catch (err) {
      console.error('Failed to load statement types:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await apiClient.get('/statement-analyzer/history?limit=10');
      setHistory(response.data.history);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!statementText.trim()) {
      setError(t('statementAnalyzer:errors.noText', 'Please enter or paste statement text to analyze'));
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      const response = await apiClient.post('/statement-analyzer/analyze', {
        statement_text: statementText,
        statement_type: selectedType || null,
        language: 'en',
      });
      setAnalysis(response.data);
      loadHistory();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('statementAnalyzer:errors.analyzeFailed', 'Failed to analyze statement'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's a PDF file - send to backend for extraction
    if (file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf') {
      setError(null);
      setAnalysis(null);
      setLoadingOverlay({
        isVisible: true,
        message: t('statementAnalyzer:analyzing', 'Analyzing Statement...'),
        subMessage: t('statementAnalyzer:analyzingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        if (selectedType) {
          formData.append('statement_type', selectedType);
        }
        formData.append('language', 'en');

        const response = await apiClient.post('/statement-analyzer/analyze-file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setAnalysis(response.data);
        loadHistory();
        setStatementText(''); // Clear text area since we uploaded a file
      } catch (err: any) {
        setError(err.response?.data?.detail || t('statementAnalyzer:errors.analyzeFailed', 'Failed to analyze PDF file'));
      } finally {
        setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      }
    } else {
      // For text files, read and display in textarea
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setStatementText(text);
      };
      reader.readAsText(file);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleViewHistory = async (historyId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/statement-analyzer/history/${historyId}`);
      setAnalysis(response.data);
      setActiveTab('analyze');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (historyId: string) => {
    if (!window.confirm(t('statementAnalyzer:confirmDelete', 'Are you sure you want to delete this analysis?'))) {
      return;
    }

    try {
      await apiClient.delete(`/statement-analyzer/history/${historyId}`);
      loadHistory();
      if (analysis?.id === historyId) {
        setAnalysis(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete analysis');
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm(t('statementAnalyzer:confirmClearAll', 'Are you sure you want to clear all analysis history?'))) {
      return;
    }

    try {
      await apiClient.delete('/statement-analyzer/history');
      setHistory([]);
      setAnalysis(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear history');
    }
  };

  const getTypeIcon = (typeId: string) => {
    const iconMap: Record<string, any> = {
      bank_statement: BuildingLibraryIcon,
      credit_card: CreditCardIcon,
      investment: ChartBarIcon,
      pay_stub: BanknotesIcon,
      tax_return: DocumentTextIcon,
      mortgage: HomeIcon,
      student_loan: AcademicCapIcon,
      other: DocumentTextIcon,
    };
    const Icon = iconMap[typeId] || DocumentTextIcon;
    return <Icon className="h-5 w-5" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Prepare chart data
  const categoryChartData = analysis
    ? Object.entries(analysis.category_breakdown).map(([category, value], index) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value: value,
        color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
      }))
    : [];

  // State for tooltip visibility
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get transactions filtered by type
  const getTransactionsByType = (type: string) => {
    if (!analysis?.transactions) return [];
    if (type === 'all') return analysis.transactions;
    return analysis.transactions.filter(t => t.type === type);
  };

  // Handle tooltip mouse events
  const handleTooltipMouseEnter = (type: string) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setActiveTooltip(type);
  };

  const handleTooltipMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setActiveTooltip(null);
    }, 200);
  };

  // Tooltip component for transaction itemization
  const TransactionTooltip: React.FC<{
    type: string;
    title: string;
    colorClass: string;
  }> = ({ type, title, colorClass }) => {
    const transactions = getTransactionsByType(type);
    if (transactions.length === 0) return null;

    return (
      <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 md:w-80
                      bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600
                      opacity-100 transition-opacity duration-200"
           onMouseEnter={() => handleTooltipMouseEnter(type)}
           onMouseLeave={handleTooltipMouseLeave}>
        <div className={`px-3 py-2 border-b border-gray-200 dark:border-gray-600 rounded-t-lg ${colorClass}`}>
          <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
            {title} ({transactions.length})
          </h4>
        </div>
        <div className="max-h-48 overflow-y-auto p-2">
          {transactions.map((t, idx) => (
            <div key={idx} className="flex justify-between items-start py-1.5 px-2 text-xs border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded">
              <div className="flex-1 pr-2">
                <p className="font-medium text-gray-800 dark:text-gray-200 truncate" title={t.description}>
                  {t.description}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-[10px]">
                  {t.date} {t.category && `• ${t.category}`}
                </p>
              </div>
              <p className={`font-semibold whitespace-nowrap ${
                type === 'income' ? 'text-green-600 dark:text-green-400' :
                type === 'fee' ? 'text-amber-600 dark:text-amber-400' :
                type === 'transfer' ? 'text-purple-600 dark:text-purple-400' :
                type === 'expense' ? 'text-red-600 dark:text-red-400' :
                'text-gray-700 dark:text-gray-300'
              }`}>
                {formatCurrency(t.amount)}
              </p>
            </div>
          ))}
        </div>
        {/* Tooltip arrow */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
          <div className="border-8 border-transparent border-t-white dark:border-t-gray-800"></div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <DocumentTextIcon className="h-8 w-8 text-emerald-500" />
            {t('statementAnalyzer:title', 'Statement Analyzer')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('statementAnalyzer:subtitle', 'Upload and analyze bank statements, credit card statements, and more')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('analyze')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'analyze'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          {t('statementAnalyzer:tabs.analyze', 'Analyze')}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <span className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4" />
            {t('statementAnalyzer:tabs.history', 'History')} ({history.length})
          </span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common:buttons.ok', 'OK')}
          </button>
        </div>
      )}

      {activeTab === 'analyze' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            {/* Statement Type Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('statementAnalyzer:selectType', 'Statement Type')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {statementTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id === selectedType ? '' : type.id)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      selectedType === type.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-emerald-300'
                    }`}
                  >
                    <div className={`mx-auto mb-1 ${selectedType === type.id ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {getTypeIcon(type.id)}
                    </div>
                    <span className={`text-xs font-medium ${selectedType === type.id ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      {type.name}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                {t('statementAnalyzer:typeOptional', 'Optional - type will be auto-detected if not selected')}
              </p>
            </div>

            {/* Text Input */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('statementAnalyzer:pasteStatement', 'Paste Statement Text')}
                </h3>
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.csv,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    {t('statementAnalyzer:uploadFile', 'Upload File')}
                  </button>
                </div>
              </div>

              <textarea
                value={statementText}
                onChange={(e) => setStatementText(e.target.value)}
                placeholder={t('statementAnalyzer:placeholder', 'Paste your statement content here, or upload a file...')}
                className="w-full h-64 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />

              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {statementText.length > 0 && `${statementText.length} characters`}
                </span>
                <div className="flex gap-3">
                  {statementText.length > 0 && (
                    <Button variant="secondary" onClick={() => setStatementText('')}>
                      {t('common:buttons.cancel', 'Clear')}
                    </Button>
                  )}
                  <Button onClick={handleAnalyze} disabled={loading || !statementText.trim()}>
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        {t('statementAnalyzer:analyzing', 'Analyzing...')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <SparklesIcon className="h-5 w-5" />
                        {t('statementAnalyzer:analyze', 'Analyze Statement')}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-12 border border-gray-200 dark:border-gray-700 flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {t('statementAnalyzer:analyzingStatement', 'Analyzing your statement...')}
                  </p>
                </div>
              </div>
            ) : analysis ? (
              <>
                {/* Summary Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <ChartBarIcon className="h-5 w-5 text-emerald-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('statementAnalyzer:summary', 'Summary')}
                      </h3>
                    </div>
                    {/* Extraction info badge */}
                    <div className="flex items-center gap-2">
                      {analysis.institution && analysis.institution !== 'unknown' && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          {analysis.institution.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      )}
                      {analysis.extraction_method === 'llm' && (
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full flex items-center gap-1">
                          <SparklesIcon className="h-3 w-3" />
                          AI Extracted
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-700 dark:text-gray-300 mb-4">{analysis.summary}</p>

                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {/* Expenses */}
                    <div
                      className="relative p-3 bg-red-50 dark:bg-red-900/20 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      onMouseEnter={() => handleTooltipMouseEnter('expense')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      {activeTooltip === 'expense' && (
                        <TransactionTooltip
                          type="expense"
                          title={t('statementAnalyzer:expenses', 'Expenses')}
                          colorClass="bg-red-50 dark:bg-red-900/30"
                        />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('statementAnalyzer:expenses', 'Expenses')}</p>
                      <p className="text-xl font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(analysis.total_expenses)}
                      </p>
                      {getTransactionsByType('expense').length > 0 && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{t('statementAnalyzer:hoverForDetails', 'Hover for details')}</p>
                      )}
                    </div>
                    {/* Income */}
                    <div
                      className="relative p-3 bg-green-50 dark:bg-green-900/20 rounded-lg cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                      onMouseEnter={() => handleTooltipMouseEnter('income')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      {activeTooltip === 'income' && (
                        <TransactionTooltip
                          type="income"
                          title={t('statementAnalyzer:income', 'Income')}
                          colorClass="bg-green-50 dark:bg-green-900/30"
                        />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('statementAnalyzer:income', 'Income')}</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(analysis.total_income)}
                      </p>
                      {getTransactionsByType('income').length > 0 && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{t('statementAnalyzer:hoverForDetails', 'Hover for details')}</p>
                      )}
                    </div>
                    {/* Fees */}
                    <div
                      className="relative p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                      onMouseEnter={() => handleTooltipMouseEnter('fee')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      {activeTooltip === 'fee' && (
                        <TransactionTooltip
                          type="fee"
                          title={t('statementAnalyzer:fees', 'Fees')}
                          colorClass="bg-amber-50 dark:bg-amber-900/30"
                        />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('statementAnalyzer:fees', 'Fees')}</p>
                      <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                        {formatCurrency(analysis.total_fees || 0)}
                      </p>
                      {getTransactionsByType('fee').length > 0 && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{t('statementAnalyzer:hoverForDetails', 'Hover for details')}</p>
                      )}
                    </div>
                    {/* Transfers */}
                    <div
                      className="relative p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      onMouseEnter={() => handleTooltipMouseEnter('transfer')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      {activeTooltip === 'transfer' && (
                        <TransactionTooltip
                          type="transfer"
                          title={t('statementAnalyzer:transfers', 'Transfers')}
                          colorClass="bg-purple-50 dark:bg-purple-900/30"
                        />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('statementAnalyzer:transfers', 'Transfers')}</p>
                      <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {formatCurrency(analysis.total_transfers || 0)}
                      </p>
                      {getTransactionsByType('transfer').length > 0 && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{t('statementAnalyzer:hoverForDetails', 'Hover for details')}</p>
                      )}
                    </div>
                    {/* All Transactions */}
                    <div
                      className="relative p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      onMouseEnter={() => handleTooltipMouseEnter('all')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      {activeTooltip === 'all' && (
                        <TransactionTooltip
                          type="all"
                          title={t('statementAnalyzer:transactions', 'All Transactions')}
                          colorClass="bg-blue-50 dark:bg-blue-900/30"
                        />
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('statementAnalyzer:transactions', 'Extracted Transactions')}</p>
                      <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                        {analysis.transaction_count}
                      </p>
                      {analysis.transaction_count > 0 && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">{t('statementAnalyzer:hoverForDetails', 'Hover for details')}</p>
                      )}
                    </div>
                  </div>

                  {/* Balance Information */}
                  {analysis.balance_info && (analysis.balance_info.current_balance !== null || analysis.balance_info.minimum_payment !== null || analysis.balance_info.credit_limit !== null) && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        {t('statementAnalyzer:balanceDetails', 'Balance Details')}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {analysis.balance_info.current_balance !== null && (
                          <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:currentBalance', 'Current Balance')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(analysis.balance_info.current_balance)}
                            </p>
                          </div>
                        )}
                        {analysis.balance_info.previous_balance !== null && (
                          <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:previousBalance', 'Previous Balance')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(analysis.balance_info.previous_balance)}
                            </p>
                          </div>
                        )}
                        {analysis.balance_info.minimum_payment !== null && (
                          <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:minimumPayment', 'Minimum Payment')}</p>
                            <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                              {formatCurrency(analysis.balance_info.minimum_payment)}
                            </p>
                          </div>
                        )}
                        {analysis.balance_info.credit_limit !== null && (
                          <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:creditLimit', 'Credit Limit')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(analysis.balance_info.credit_limit)}
                            </p>
                          </div>
                        )}
                        {analysis.balance_info.available_credit !== null && (
                          <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:availableCredit', 'Available Credit')}</p>
                            <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(analysis.balance_info.available_credit)}
                            </p>
                          </div>
                        )}
                        {analysis.balance_info.payment_due_date && (
                          <div className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('statementAnalyzer:paymentDueDate', 'Payment Due')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {analysis.balance_info.payment_due_date}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Category Breakdown */}
                {categoryChartData.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      {t('statementAnalyzer:categoryBreakdown', 'Spending by Category')}
                    </h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={categoryChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {categoryChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {categoryChartData.map((item, index) => (
                        <span key={index} className="flex items-center gap-1 text-xs">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          {item.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Insights */}
                {analysis.insights && analysis.insights.length > 0 && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
                    <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 mb-3 flex items-center gap-2">
                      <LightBulbIcon className="h-5 w-5" />
                      {t('statementAnalyzer:insights', 'Insights')}
                    </h3>
                    <ul className="space-y-2">
                      {analysis.insights.map((insight, index) => (
                        <li key={index} className="flex items-start gap-2 text-emerald-700 dark:text-emerald-300">
                          <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Opportunities */}
                {analysis.opportunities && analysis.opportunities.length > 0 && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                    <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                      <SparklesIcon className="h-5 w-5" />
                      {t('statementAnalyzer:opportunities', 'Money-Saving Opportunities')}
                    </h3>
                    <ul className="space-y-2">
                      {analysis.opportunities.map((opp, index) => (
                        <li key={index} className="flex items-start gap-2 text-blue-700 dark:text-blue-300">
                          <span>💡</span>
                          {opp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {analysis.warnings && analysis.warnings.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
                    <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5" />
                      {t('statementAnalyzer:warnings', 'Warnings')}
                    </h3>
                    <ul className="space-y-2">
                      {analysis.warnings.map((warning, index) => (
                        <li key={index} className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                          <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Items */}
                {analysis.action_items && analysis.action_items.length > 0 && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                    <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-3">
                      {t('statementAnalyzer:actionItems', 'Recommended Actions')}
                    </h3>
                    <ol className="space-y-2">
                      {analysis.action_items.map((action, index) => (
                        <li key={index} className="flex items-start gap-3 text-purple-700 dark:text-purple-300">
                          <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          {action}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-12 border border-gray-200 dark:border-gray-700 text-center">
                <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t('statementAnalyzer:noAnalysis', 'Paste or upload a statement to get started')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('statementAnalyzer:analysisHistory', 'Analysis History')}
            </h2>
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
                {t('statementAnalyzer:clearAll', 'Clear All')}
              </button>
            )}
          </div>

          {history.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                          {getTypeIcon(item.statement_type)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {item.statement_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(item.analysis_date)} • {item.transaction_count} transactions
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500 dark:text-gray-400">{t('statementAnalyzer:expenses', 'Expenses')}</p>
                          <p className="font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(item.total_expenses)}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleViewHistory(item.id)}
                            className="p-2 text-gray-500 hover:text-emerald-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title={t('statementAnalyzer:view', 'View')}
                          >
                            <ChartBarIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteHistory(item.id)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title={t('common:buttons.delete', 'Delete')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-12 border border-gray-200 dark:border-gray-700 text-center">
              <ClockIcon className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('statementAnalyzer:noHistory', 'No analysis history yet. Analyze a statement to get started.')}
              </p>
              <Button onClick={() => setActiveTab('analyze')} className="mt-4">
                {t('statementAnalyzer:analyzeFirst', 'Analyze a Statement')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('common:disclaimer.financial', 'This information is for educational purposes only and is not financial advice. Please consult a qualified financial professional for personalized guidance.')}
        </p>
      </div>
    </div>
  );
};

export default StatementAnalyzer;
